// views/DeliveryFee/FeeBreakdown.jsx
// ─────────────────────────────────────────────────────────────────────────────
// "How this fee is computed" — a visual waterfall of Contract A's `breakdown`.
//
// Reads the preview response (services/deliveryFeeCalculator.js → quoteDelivery)
// and renders the buildup the engine actually performs:
//
//   Base fare
//     + distance bands  (each cumulative tier: km × rate)
//     ───────────────── = pre-multiplier subtotal
//     × surge           (with surgeReason)
//     × route complexity(with complexityReason)
//     + convenience fee
//     ───────────────── = subtotal
//     → margin floor / min fee / soft cap clamp
//   = FINAL FEE
//
// The zone (urban / peri-urban / UPCOUNTRY) is shown prominently up top.
//
// Role-awareness: when `showCost` is true (super-admins only) the COST model and
// margin% are rendered in a separate "internal economics" block. Vendors never
// receive — and never see — the `cost` block.
// ─────────────────────────────────────────────────────────────────────────────

import React from "react";
import {
  FiMapPin, FiTruck, FiZap, FiSliders, FiPlusCircle, FiCornerDownRight,
  FiShield, FiTrendingUp, FiLock, FiArrowDown, FiCheckCircle, FiPercent,
} from "react-icons/fi";
import {
  C, zoneMeta, formatUGX, formatSignedUGX, formatKm, formatMultiplier,
  formatPct, badgeStyle,
} from "./feeUiKit.js";

// ── Local style atoms ────────────────────────────────────────────────────────
const row = (opts = {}) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: opts.tall ? "11px 14px" : "9px 14px",
  borderRadius: C.radiusSm,
  background: opts.bg || "transparent",
  border: opts.border ? `1px solid ${opts.border}` : "1px solid transparent",
  marginBottom: 4,
  fontSize: 13,
});

const labelWrap = { display: "flex", alignItems: "center", gap: 9, minWidth: 0 };

const iconChip = (color, soft) => ({
  width: 28,
  height: 28,
  borderRadius: 7,
  background: soft,
  color,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  fontSize: 13,
});

const reasonText = {
  fontSize: 11,
  color: C.textLight,
  marginTop: 1,
  lineHeight: 1.3,
};

const connector = {
  width: 2,
  height: 10,
  background: C.border,
  marginLeft: 27,
  marginTop: -2,
  marginBottom: -2,
};

// A single buildup line: icon + label (+ optional reason) on the left, amount right.
function BuildRow({ icon, color, soft, label, reason, amount, amountColor, bg, border, strong, tall }) {
  return (
    <div style={row({ bg, border, tall })}>
      <div style={labelWrap}>
        {icon && <span style={iconChip(color, soft)}>{icon}</span>}
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: strong ? 700 : 500, color: C.text }}>{label}</div>
          {reason ? <div style={reasonText}>{reason}</div> : null}
        </div>
      </div>
      <div style={{ fontWeight: strong ? 800 : 700, color: amountColor || C.text, whiteSpace: "nowrap", fontSize: strong ? 15 : 13 }}>
        {amount}
      </div>
    </div>
  );
}

function Connector() {
  return <div style={connector} />;
}

// Subtotal divider line with a label + running total.
function SubtotalLine({ label, value, color = C.textMid }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      borderTop: `1px dashed ${C.border}`, margin: "6px 0 8px", padding: "8px 14px 0",
      fontSize: 12, color,
    }}>
      <span style={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px" }}>{label}</span>
      <span style={{ fontWeight: 800, color: C.text }}>{value}</span>
    </div>
  );
}

export default function FeeBreakdown({ quote, showCost = false }) {
  if (!quote || !quote.breakdown) return null;

  const b = quote.breakdown;
  const zm = zoneMeta(quote.zone);
  const tiers = Array.isArray(b.tiers) ? b.tiers : [];

  const surge = Number(b.surgeMultiplier ?? 1);
  const cx = Number(b.complexityMultiplier ?? 1);
  const hasSurge = surge && Math.abs(surge - 1) > 0.001;
  const hasCx = cx && Math.abs(cx - 1) > 0.001;

  // The clamp the engine applied (if any). `appliedFloor` names the binding
  // constraint: 'margin' | 'min' | 'soft_cap' | null.
  const applied = b.appliedFloor;
  const clampMeta = {
    margin:   { label: "Margin floor applied", icon: <FiShield size={13} />, color: C.orange, soft: C.orangeSoft, value: b.marginFloor },
    min:      { label: "Minimum fee applied",  icon: <FiLock size={13} />,   color: C.blue,   soft: C.blueSoft,   value: b.minFee },
    soft_cap: { label: "Soft cap applied",     icon: <FiTrendingUp size={13} />, color: C.teal, soft: C.tealSoft, value: b.softCap },
  }[applied];

  return (
    <div>
      {/* ── Zone banner ── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 12, flexWrap: "wrap",
        background: zm.soft, border: `1px solid ${zm.color}33`,
        borderRadius: C.radius, padding: "12px 16px", marginBottom: 16,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={iconChip(zm.color, "#ffffff")}><FiMapPin size={14} /></span>
          <div>
            <div style={{ fontSize: 10, color: C.textLight, textTransform: "uppercase", letterSpacing: "0.6px", fontWeight: 700 }}>
              Pricing zone
            </div>
            <div style={{ fontSize: 16, fontWeight: 800, color: zm.color, letterSpacing: "-0.2px" }}>
              {zm.label}
            </div>
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 11, color: C.textMid }}>
            {formatKm(quote.distanceKm)} · ~{Math.round(Number(quote.durationMin || 0))} min
          </div>
          <div style={{ fontSize: 11, color: C.textLight, maxWidth: 240 }}>{zm.desc}</div>
        </div>
      </div>

      {/* ── Waterfall buildup ── */}
      <BuildRow
        icon={<FiTruck size={13} />}
        color={C.blue} soft={C.blueSoft}
        label="Base fare"
        reason={`Fixed pickup/handling charge for the ${zm.label.toLowerCase()} zone`}
        amount={formatUGX(b.baseFare)}
      />
      <Connector />

      {/* Distance tiers — cumulative bands, each shown as km × rate */}
      {tiers.length > 0 ? (
        tiers.map((t, i) => {
          const from = Number(t.fromKm ?? 0);
          const to = t.toKm == null ? "∞" : Number(t.toKm);
          const km = Number(t.km ?? 0);
          const rate = Number(t.rate ?? 0);
          return (
            <React.Fragment key={`tier-${i}`}>
              <BuildRow
                icon={<FiCornerDownRight size={13} />}
                color={C.teal} soft={C.tealSoft}
                label={`Distance band ${from}–${to} km`}
                reason={`${km.toFixed(1)} km in this band × UGX ${rate.toLocaleString()}/km`}
                amount={formatSignedUGX(t.amount)}
                amountColor={C.teal}
              />
              <Connector />
            </React.Fragment>
          );
        })
      ) : (
        <>
          <BuildRow
            icon={<FiCornerDownRight size={13} />}
            color={C.teal} soft={C.tealSoft}
            label="Distance charge"
            reason={formatKm(quote.distanceKm)}
            amount={formatSignedUGX(b.distanceCharge)}
            amountColor={C.teal}
          />
          <Connector />
        </>
      )}

      <SubtotalLine
        label="Distance subtotal"
        value={formatUGX(Number(b.baseFare || 0) + Number(b.distanceCharge || 0))}
      />

      {/* Surge multiplier */}
      <BuildRow
        icon={<FiZap size={13} />}
        color={hasSurge ? C.accent : C.textLight}
        soft={hasSurge ? C.accentSoft : "#f0f2f5"}
        label={`Surge ${formatMultiplier(surge)}`}
        reason={b.surgeReason || (hasSurge ? "Demand-based multiplier" : "No surge — standard demand")}
        amount={formatMultiplier(surge)}
        amountColor={hasSurge ? C.accent : C.textLight}
      />
      <Connector />

      {/* Route-complexity multiplier */}
      <BuildRow
        icon={<FiSliders size={13} />}
        color={hasCx ? C.purple : C.textLight}
        soft={hasCx ? C.purpleSoft : "#f0f2f5"}
        label={`Route complexity ${formatMultiplier(cx)}`}
        reason={b.complexityReason || (hasCx ? "Pickup-difficulty multiplier" : "Standard access — no uplift")}
        amount={formatMultiplier(cx)}
        amountColor={hasCx ? C.purple : C.textLight}
      />
      <Connector />

      {/* Convenience fee */}
      <BuildRow
        icon={<FiPlusCircle size={13} />}
        color={C.orange} soft={C.orangeSoft}
        label="Convenience fee"
        reason="Flat platform service fee"
        amount={formatSignedUGX(b.convenienceFee)}
        amountColor={C.orange}
      />

      <SubtotalLine label="Subtotal (pre-clamp)" value={formatUGX(b.subtotal)} />

      {/* Clamp explanation — only when a floor/cap actually bound the fee */}
      {clampMeta ? (
        <>
          <div style={{ display: "flex", justifyContent: "center", margin: "2px 0" }}>
            <FiArrowDown size={16} color={clampMeta.color} />
          </div>
          <BuildRow
            icon={clampMeta.icon}
            color={clampMeta.color} soft={clampMeta.soft}
            label={clampMeta.label}
            reason={
              applied === "margin"
                ? "Subtotal fell below the cost-aware floor — the floor was used so the trip is not loss-making."
                : applied === "min"
                ? "Short trip — the zone's minimum fee was applied."
                : "Long leg — the fare was trimmed to the zone's soft cap so the customer isn't overcharged."
            }
            amount={formatUGX(clampMeta.value)}
            amountColor={clampMeta.color}
            bg={clampMeta.soft}
            border={`${clampMeta.color}40`}
          />
        </>
      ) : (
        <div style={{ fontSize: 11, color: C.textLight, textAlign: "center", padding: "4px 0 8px", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
          <FiCheckCircle size={12} color={C.teal} />
          Subtotal sits within the zone's floor and cap — no clamp needed.
        </div>
      )}

      {/* ── Final fee ── */}
      <BuildRow
        icon={<FiCheckCircle size={15} />}
        color="#fff" soft={C.teal}
        label="Final delivery fee"
        amount={formatUGX(quote.fee)}
        amountColor={C.teal}
        bg={C.tealSoft}
        border={`${C.teal}55`}
        strong
        tall
      />

      {/* ── Super-admin only: internal cost model + realised margin ── */}
      {showCost && quote.cost ? (
        <div style={{
          marginTop: 18, border: `1px dashed ${C.accentBorder}`,
          borderRadius: C.radius, padding: "14px 16px", background: "#fffafb",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <FiShield size={13} color={C.accent} />
            <span style={{ fontSize: 11, fontWeight: 800, color: C.accent, textTransform: "uppercase", letterSpacing: "0.6px" }}>
              Internal economics
            </span>
            <span style={{ ...badgeStyle(C.accent, C.accentSoft), marginLeft: "auto" }}>
              Super-admin only
            </span>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {[
              { label: "Fixed cost",    val: formatUGX(quote.cost.fixed) },
              { label: "Per-km cost",   val: `UGX ${Number(quote.cost.perKm || 0).toLocaleString()}/km` },
              { label: "Distance cost", val: formatUGX(quote.cost.distanceCost) },
              { label: "Total cost",    val: formatUGX(quote.cost.total) },
            ].map(({ label, val }) => (
              <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "5px 0", borderBottom: `1px solid ${C.border}` }}>
                <span style={{ color: C.textMid }}>{label}</span>
                <span style={{ fontWeight: 700, color: C.text }}>{val}</span>
              </div>
            ))}
          </div>

          {quote.cost.model ? (
            <div style={{ fontSize: 10, color: C.textLight, marginTop: 6 }}>
              Cost model: <code style={{ fontSize: 10 }}>{quote.cost.model}</code>
            </div>
          ) : null}

          {/* Realised margin */}
          {quote.margin ? (
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              marginTop: 10, padding: "10px 12px", borderRadius: C.radiusSm,
              background: Number(quote.margin.pct) >= 0 ? C.tealSoft : C.accentSoft,
              border: `1px solid ${Number(quote.margin.pct) >= 0 ? C.teal : C.accent}33`,
            }}>
              <span style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, fontWeight: 700, color: Number(quote.margin.pct) >= 0 ? C.teal : C.accent }}>
                <FiPercent size={13} /> Realised margin
              </span>
              <span style={{ fontSize: 14, fontWeight: 800, color: Number(quote.margin.pct) >= 0 ? C.teal : C.accent }}>
                {formatUGX(quote.margin.amount)} · {formatPct(quote.margin.pct)}
              </span>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
